import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/client/lib/auth";
import { prefetchTab, type PortalTab } from "@/client/lib/portal";
import { HomeIcon, ClipboardListIcon, TheaterIcon, MailIcon, LogOutIcon } from "lucide-react";

export type PortalPhase = "data" | "waiting" | "selection" | "complete";

// Extra props are accepted (callers still pass schoolName/save/phase) but intentionally
// unused: the header is now a single, flat nav bar, nothing more.
type PortalNavProps = {
  schoolName?: string;
  schoolId?: number;
  phase?: PortalPhase;
  saveLabel?: string | null;
  saveError?: boolean;
  unreadCount?: number;
};

const NAV_ITEMS = (schoolId: number | undefined) => {
  if (!schoolId) return [];
  return [
    { label: "Overzicht", href: `/school/${schoolId}/overzicht`, icon: HomeIcon, match: "/overzicht" },
    { label: "Schoolgegevens", href: `/school/${schoolId}/schoolgegevens`, icon: ClipboardListIcon, match: "/schoolgegevens" },
    { label: "Programmaselectie", href: `/school/${schoolId}/programmaselectie`, icon: TheaterIcon, match: "/programmaselectie" },
    { label: "Contact", href: `/school/${schoolId}/contact`, icon: MailIcon, match: "/contact" },
  ];
};

// Which nav targets have a prefetchable data tab. Contact is static (no fetch), so it is
// intentionally absent and its hover/focus prefetch is a no-op.
const PREFETCH_TABS: PortalTab[] = ["overzicht", "schoolgegevens", "programmaselectie"];

export function PortalNav({ schoolId }: PortalNavProps) {
  const { logout } = useAuth();
  const location = useLocation();
  const navItems = NAV_ITEMS(schoolId);
  // Match on a whole path SEGMENT, not a substring: otherwise "/contact" matches inside
  // "/schoolgegevens/contactgegevens" and lights up the Contact tab on the schoolgegevens page.
  const isActive = (match: string) => location.pathname.split("/").includes(match.slice(1));

  return (
    <header className="border-b border-border bg-background">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 sm:px-8">
        <nav className="-mb-px -ml-4 flex gap-1 overflow-x-auto" aria-label="Hoofdnavigatie">
          {navItems.map((item) => {
            const active = isActive(item.match);
            const tab = item.match.slice(1) as PortalTab;
            const prefetch = () => { if (schoolId && PREFETCH_TABS.includes(tab)) prefetchTab(schoolId, tab); };
            return (
              <Link
                key={item.href}
                to={item.href}
                onPointerEnter={prefetch}
                onFocus={prefetch}
                onPointerDown={prefetch}
                className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3.5 text-sm font-medium transition-colors ${
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                }`}
              >
                <item.icon className="size-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <button
          type="button"
          onClick={logout}
          className="flex shrink-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Uitloggen"
        >
          <LogOutIcon className="size-4" />
          <span className="hidden sm:inline">Uitloggen</span>
        </button>
      </div>
    </header>
  );
}
